import { Box } from '@superdispatch/ui-lab';
import { omit, round } from 'es-toolkit/compat';
import { useMemo } from 'react';
import { useAppFormik } from 'shared/form/AppFormik';
import {
  FormikDrawer,
  FormikDrawerActions,
  FormikDrawerContent,
} from 'shared/form/FormikDrawer';
import { computePrice } from 'shared/utils/NumberUtils';
import { isEmptyLoadPayment } from '../data/LoadActionsDTO';
import { formatLoadStatus, loadPaymentSchema } from '../data/LoadDTO';
import { trackLoadsEvent } from '../data/LoadsAnalytics';
import { useLoadDetails, useLoadsAPI, useLoadsCache } from '../data/LoadsAPI';
import { useLoadStageFromBackURL } from '../view/ViewLoadHeaderActions';
import { PaymentAgreementForm } from './PaymentAgreementForm';
import { useSubhaulLoad } from '../view/useSubhaulLoad';

interface EditPaymentAgreementDrawerContentProps {
  loadGUID: string;
  onClose: () => void;
  isEnabledEditLoad: boolean;
}

function EditPaymentAgreementDrawerContent({
  onClose,
  loadGUID,
  isEnabledEditLoad,
}: EditPaymentAgreementDrawerContentProps) {
  const { isSubhauled } = useSubhaulLoad();
  const { data } = useLoadDetails(loadGUID, { isSubhauled });
  const { editLoadPayment } = useLoadsAPI();
  const { invalidateLoads } = useLoadsCache();
  const loadStage = useLoadStageFromBackURL();

  const formik = useAppFormik({
    initialValues: {
      ...data?.payments[0],
      payment_details: data?.payment_details,
    },
    validationSchema: loadPaymentSchema,
    onSubmit(values) {
      const formValues = omit(values, ['paid_at']);
      return editLoadPayment(loadGUID, {
        ...formValues,
        receipt_date: values.paid_at,
        price: round(values.price ?? 0, 2),
      });
    },
    onSubmitSuccess() {
      onClose();
      invalidateLoads();

      trackLoadsEvent({
        name: 'Carrier Edited Load',
        utm_medium: loadStage,
        utm_content: 'View Page',
        load_status: formatLoadStatus(data?.status),
        is_created_by_broker: !!data?.is_created_by_broker,
        payment_information: isEmptyLoadPayment(data?.payments[0])
          ? 'Added'
          : 'Edited',
      });
    },
    getSuccessMessage() {
      return 'Payment information successfully updated';
    },
    getErrorMessage({ message }) {
      return message || 'Something went wrong, contact support.';
    },
  });

  const vehiclesTotalPrice = useMemo(
    () => computePrice(data?.vehicles),
    [data],
  );

  return (
    <FormikDrawerContent
      title="Payment Agreement"
      formik={formik}
      onClose={onClose}
      actions={<FormikDrawerActions name="loads" permission="can_manage" />}
    >
      <Box maxWidth="368px">
        {data && (
          <PaymentAgreementForm
            vehiclesTotalPrice={vehiclesTotalPrice}
            canPaymentBeEdited={!!data.can_payment_be_edited}
            isEnabledEditLoad={isEnabledEditLoad}
            isReceivedViaSubhaul={data.is_received_via_subhaul}
          />
        )}
      </Box>
    </FormikDrawerContent>
  );
}

interface EditPaymentAgreementDrawerProps extends EditPaymentAgreementDrawerContentProps {
  open: boolean;
  isEnabledEditLoad: boolean;
}

export function EditPaymentAgreementDrawer({
  open,
  onClose,
  loadGUID,
  isEnabledEditLoad,
}: EditPaymentAgreementDrawerProps) {
  return (
    <FormikDrawer open={open} onClose={onClose}>
      <EditPaymentAgreementDrawerContent
        onClose={onClose}
        loadGUID={loadGUID}
        isEnabledEditLoad={isEnabledEditLoad}
      />
    </FormikDrawer>
  );
}
